import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { HelpCircle, RefreshCw } from 'lucide-react'
import type {
  CaseEndpoint,
  CreateRelationshipRequest,
  QuestionTemplate,
} from '../../../../types/case-container'
import {
  CONNECTION_TYPES,
  QUESTION_PREVIEW_LENGTH,
  RELATIONSHIP_STATUSES,
  groupConnectionTypes,
} from '../../../../components/infrastructure/bulk-create-link-modal/bulk-create-link-modal.constants'

interface DirectionPreviewProps {
  sourceEndpoint: CaseEndpoint
  selectedTarget: CaseEndpoint | undefined
}

export function DirectionPreview({
  sourceEndpoint,
  selectedTarget,
}: DirectionPreviewProps): ReactElement {
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="mb-2 text-xs tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Direction (Source → Target)
      </p>
      <div className="flex items-center gap-2 text-sm">
        <span className="rounded bg-lime-100 px-2 py-1 font-medium text-[#5c6e94] dark:bg-lime-950/50 dark:text-lime-400">
          {sourceEndpoint.hostname}
        </span>
        <span className="text-slate-400">→</span>
        <span
          className={`rounded px-2 py-1 font-medium ${selectedTarget ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}
        >
          {selectedTarget?.hostname ?? 'Select target...'}
        </span>
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        The source is the &quot;parent&quot; or origin of the relationship.
      </p>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string }): ReactElement {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

interface TargetSelectProps {
  availableTargets: CaseEndpoint[]
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
}

export function TargetEndpointSelect({
  availableTargets,
  formData,
  setFormData,
}: TargetSelectProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        Target Endpoint *
      </label>
      {availableTargets.length === 0 ? (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          No other endpoints available. Add more endpoints first.
        </p>
      ) : (
        <select
          value={formData.targetEndpointId}
          onChange={(e) => {
            setFormData({ ...formData, targetEndpointId: e.target.value })
          }}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          required
        >
          <option value="">Select target endpoint...</option>
          {availableTargets.map((ep) => (
            <option key={ep.id} value={ep.id}>
              {ep.hostname} ({ep.deviceType.replaceAll('_', ' ')})
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

interface ConnectionTypeSelectProps {
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
}

export function ConnectionTypeSelect({
  formData,
  setFormData,
}: ConnectionTypeSelectProps): ReactElement {
  const groupedTypes = groupConnectionTypes()
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        Connection Type *
      </label>
      <select
        value={formData.connectionType}
        onChange={(e) => {
          setFormData({ ...formData, connectionType: e.target.value })
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        {Object.entries(groupedTypes).map(([category, types]) => (
          <optgroup key={category} label={category}>
            {types.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {CONNECTION_TYPES.find((ct) => ct.value === formData.connectionType)?.description}
      </p>
    </div>
  )
}

interface StatusButtonsProps {
  formData: CreateRelationshipRequest
  isOpenQuestion: boolean
  handleStatusChange: (status: string) => void
}

export function StatusButtons({
  formData,
  isOpenQuestion,
  handleStatusChange,
}: StatusButtonsProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        Status
      </label>
      <div className="flex gap-2">
        {RELATIONSHIP_STATUSES.map((status) => {
          const active =
            formData.status === status.value || (isOpenQuestion && status.value === 'open-question')
          const cls = active
            ? status.value === 'open-question'
              ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400'
              : 'border-[#4e5e82] bg-[#4e5e82]/5 dark:bg-[#8a9bba]/15 text-[#5c6e94] dark:text-lime-400'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
          return (
            <button
              key={status.value}
              type="button"
              onClick={() => {
                handleStatusChange(status.value)
              }}
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${cls}`}
            >
              {status.value === 'open-question' && (
                <HelpCircle className="mr-1 inline h-3.5 w-3.5" />
              )}
              {status.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface LabelInputProps {
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
  t: TFunction<'cases'>
}

export function LabelInput({ formData, setFormData, t }: LabelInputProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        Label <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <input
        type="text"
        value={formData.label ?? ''}
        onChange={(e) => {
          setFormData({ ...formData, label: e.target.value })
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.placeholders.relationshipTitle')}
      />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        A short descriptive name for this specific relationship.
      </p>
    </div>
  )
}

interface OpenQuestionSectionProps {
  templates: QuestionTemplate[]
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
  t: TFunction<'cases'>
}

export function OpenQuestionSection({
  templates,
  formData,
  setFormData,
  t,
}: OpenQuestionSectionProps): ReactElement {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-2 flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
          Open Question
        </span>
      </div>
      <p className="mb-3 text-xs text-amber-600 dark:text-amber-500">
        Document what needs to be investigated about this relationship.
      </p>
      {templates.length > 0 && (
        <TemplateSelector templates={templates} formData={formData} setFormData={setFormData} />
      )}
      <textarea
        value={formData.investigationNotes ?? ''}
        onChange={(e) => {
          setFormData({ ...formData, investigationNotes: e.target.value })
        }}
        rows={3}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.placeholders.openQuestionText')}
      />
    </div>
  )
}

interface TemplateSelectorProps {
  templates: QuestionTemplate[]
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
}

function TemplateSelector({
  templates,
  formData,
  setFormData,
}: TemplateSelectorProps): ReactElement {
  const grouped = templates.reduce<Record<string, QuestionTemplate[]>>((acc, tpl) => {
    const cat = tpl.category ?? 'Uncategorised'
    acc[cat] ??= []
    acc[cat].push(tpl)
    return acc
  }, {})
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-medium text-amber-700 dark:text-amber-400">
        Use Template
      </label>
      <select
        onChange={(e) => {
          const template = templates.find((tpl) => tpl.id === e.target.value)
          if (template) setFormData({ ...formData, investigationNotes: template.questionText })
        }}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">Select a template...</option>
        {Object.entries(grouped).map(([category, categoryTemplates]) => (
          <optgroup key={category} label={category}>
            {categoryTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.questionText.slice(0, QUESTION_PREVIEW_LENGTH)}
                {tpl.questionText.length > QUESTION_PREVIEW_LENGTH ? '...' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  )
}

interface NotesFieldProps {
  formData: CreateRelationshipRequest
  setFormData: React.Dispatch<React.SetStateAction<CreateRelationshipRequest>>
  t: TFunction<'cases'>
}

export function NotesField({ formData, setFormData, t }: NotesFieldProps): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        Investigation Notes <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <textarea
        value={formData.investigationNotes ?? ''}
        onChange={(e) => {
          setFormData({ ...formData, investigationNotes: e.target.value })
        }}
        rows={2}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.placeholders.relationshipNotes')}
      />
    </div>
  )
}

interface BidirectionalToggleProps {
  bidirectional: boolean
  setBidirectional: React.Dispatch<React.SetStateAction<boolean>>
}

export function BidirectionalToggle({
  bidirectional,
  setBidirectional,
}: BidirectionalToggleProps): ReactElement {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <input
        type="checkbox"
        id="bidirectional"
        checked={bidirectional}
        onChange={(e) => {
          setBidirectional(e.target.checked)
        }}
        className="h-4 w-4 rounded border-slate-300 text-lime-600 focus:ring-[#4e5e82] dark:border-slate-600"
      />
      <div>
        <label
          htmlFor="bidirectional"
          className="text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          Create bidirectional link
        </label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Also create reverse relationship (target → source)
        </p>
      </div>
    </div>
  )
}

interface FormActionsProps {
  onClose: () => void
  creating: boolean
  formData: CreateRelationshipRequest
  availableTargetsCount: number
}

export function FormActions({
  onClose,
  creating,
  formData,
  availableTargetsCount,
}: FormActionsProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={creating || !formData.targetEndpointId || availableTargetsCount === 0}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {creating && <RefreshCw className="h-4 w-4 animate-spin" />}
        Create Relationship
      </button>
    </div>
  )
}
